import { useState } from 'react';
import { useTheme } from '@emotion/react';
import Alert from '@mui/material/Alert';
import { Button, Text } from 'components';
import { Trans, t } from '@lingui/macro';
import styled from '@emotion/styled';
import { Buildings, User, ArrowLeft } from '@phosphor-icons/react';
import { AuthLink } from '../AuthLink';
import { AuthButton } from '../AuthButton';
import { InputField, Form, useForm } from 'features/form';
import type { Fields } from 'features/form';
import * as Yup from 'yup';
import { useSupplierSignUp } from '../../api/useSupplierSignUp';
import { LoginExistingUser } from './LoginExistingUser';
import { EmailChip, ProgressDots, ProgressDot } from './SharedComponents';

const VIEW_EXISTING = 'existing' as const;
const VIEW_NEW_ACCOUNT = 'newAccount' as const;

const companyNameValidation = Yup.string()
  .required(t`Please enter a company name`)
  .max(255, t`Company name is too long`);

const validationsExistingUser = Yup.object().shape({
  companyName: companyNameValidation,
  yourName: Yup.string()
    .required(t`Please enter your name`)
    .max(255, t`Your name is too long`),
});

const formFieldsExistingUser: Fields = {
  companyName: {
    type: 'text',
    id: 'companyName',
    required: true,
    placeholder: t`Enter company name`,
    icon: <Buildings />,
    autoComplete: 'organization',
  },
  yourName: {
    type: 'text',
    id: 'yourName',
    required: true,
    placeholder: t`Enter your name`,
    icon: <User />,
    autoComplete: 'name',
  },
};

export const SignUpExistingUser = () => {
  const { signupResponse, signUp2ndStep, isPending2ndStep, companySignIn, clearSignupResponse } =
    useSupplierSignUp();
  const [view, setView] = useState<typeof VIEW_EXISTING | typeof VIEW_NEW_ACCOUNT>(VIEW_EXISTING);

  const accountCount = signupResponse?.userAssociatedSupplierAccounts?.length ?? 0;

  if (view === VIEW_NEW_ACCOUNT) {
    return (
      <NewSupplierAccountFormView
        signupResponse={signupResponse}
        accountCount={accountCount}
        onBack={() => setView(VIEW_EXISTING)}
        onSuccess={signUp2ndStep}
        isPending={isPending2ndStep}
        onCancel={clearSignupResponse}
      />
    );
  }

  return (
    <LoginExistingUser
      onSelectCompany={(id, name) => companySignIn(id, name)}
      onCreateNewAccount={() => setView(VIEW_NEW_ACCOUNT)}
    />
  );
};

type NewSupplierAccountFormViewProps = {
  signupResponse: ReturnType<typeof useSupplierSignUp>['signupResponse'];
  accountCount: number;
  onBack: () => void;
  onSuccess: (args: { data: { loginId: string; companyName: string; userName: string } }) => void;
  isPending: boolean;
  onCancel: () => void;
};

const NewSupplierAccountFormView = ({
  signupResponse,
  accountCount,
  onBack,
  onSuccess,
  isPending,
  onCancel,
}: NewSupplierAccountFormViewProps) => {
  const theme = useTheme();
  const onSubmit = async () => {
    const result = await submit();
    if (result.errors.length === 0) {
      onSuccess({
        data: {
          loginId: signupResponse?.id || '',
          companyName: String(result.data.companyName),
          userName: String(result.data.yourName),
        },
      });
    }
  };
  const { fields, submit, disableBtn } = useForm({
    fields: formFieldsExistingUser,
    validations: validationsExistingUser,
  });
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();
      }}
    >
      <Form validations={validationsExistingUser} width="100%">
        <FormContainer>
          <EmailChip email={signupResponse?.email} />
          <ProgressDots>
            <ProgressDot active={false} />
            <ProgressDot active />
          </ProgressDots>
          <Text variant="headlineMd" weight="semiBold" noWrap>
            <Trans>New supplier account</Trans>
          </Text>
          {accountCount > 0 ? (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                marginBottom: theme.s.xs,
              }}
            >
              <Trans>
                Are you sure you need a new account? You already have {accountCount} supplier
                account(s) under this email. Only create a new one if you&apos;re representing a
                different supplier company.
              </Trans>
            </Alert>
          ) : null}
          <InputField field={fields.yourName} w={100} />
          <InputField
            field={{
              ...fields.companyName,
              helperText: t`Fill out the name of your company to create another account.`,
            }}
            w={100}
          />
        </FormContainer>
      </Form>
      <ButtonGroup>
        <AuthButton
          disabled={disableBtn}
          isLoading={isPending}
          buttonText={t`Create new account`}
        />
        <Button
          size="lg"
          variant="secondary"
          color="primary"
          startIcon={<ArrowLeft size={18} />}
          onClick={onBack}
          sx={{
            margin: `0 ${theme.s.lg}`,
            '& .MuiButton-startIcon': {
              color: theme.palette.text.brandSecondary,
              '& svg': {
                fill: theme.palette.text.brandSecondary,
                stroke: theme.palette.text.brandSecondary,
              },
            },
          }}
        >
          <Trans>Back to existing accounts</Trans>
        </Button>
        <AuthLink
          linkTo="/supplier/signup"
          questionText=""
          linkText={t`Cancel sign up`}
          onClick={onCancel}
          light
        />
      </ButtonGroup>
    </form>
  );
};

const FormContainer = styled.div`
  margin: ${({ theme }) => theme.s.lg};
  margin-bottom: ${({ theme }) => theme.s.md};
  gap: ${({ theme }) => theme.s.xs};
  display: flex;
  flex-direction: column;
  width: calc(100% - ${({ theme }) => theme.s.xxl});
  svg {
    fill: ${p => p.theme.palette.fg.primary};
  }
  .MuiAlert-icon svg {
    fill: currentColor;
  }
  .MuiFormHelperText-root {
    font-size: ${p => p.theme.typography.bodyXs.fontSize};
    color: ${p => p.theme.palette.text.tertiary};
    line-height: ${({ theme }) => theme.s.md};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.s.xs};
`;
